import React, { Fragment, useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import URI from 'urijs';
import { ICONS } from 'Components/utils/icon/icons';
import { onCheckboxChange } from 'Components/widgets/profile-info/newsletter-subscription/NewsletterSubscription.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { NEWSLETTER_SUBSCRIPTION_PAGE_ID } from 'Routes/constants';
import { addTranslationKeys, t } from 'Src/i18n';
import { STATIC_PAGE_TYPES, TRACK_EVENT_NAMES, TRACK_PAGE_NAMES } from 'Tracking/constants';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { NEWSLETTER_SUBSCRIPTION_VIEW_RESOLVER } from 'Utils/request/requestDictionary';
import { ORIGIN_PLE, SUBSCRIPTION_ORIGIN } from 'Views/booking/constants';
import { subscribeQuery } from 'Views/newsletter/Newsletter.queries';
import {
  formatDataForSubscription,
  submitSubscriptionForm,
} from 'Views/newsletter/newsletterActions';
import { Button } from 'Components/frameworks/button/Button';
import { FormErrors } from 'Components/frameworks/form-errors/FormErrors';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { Hero } from 'Components/widgets/hero/Hero';
import {
  NewsletterForm,
  subscriptionSchema,
} from 'Components/widgets/newsletter-form/NewsletterForm';
export const SubscribeView = () => {
  const [submitErrors, setSubmitErrors] = useState([]);
  const appContext = useAppContext();
  const router = useRouter();
  const { subscriptionId, locale } = router.query;
  const shouldDisplaySubscriptionView = isFeatureFlipped(appContext)(
    'newsletter.displaySubscriptionView',
  );
  useEffect(() => {
    if (!shouldDisplaySubscriptionView && !!router) {
      const link = '/_error?statusCode=404';
      const url = locale
        ? new URI(link)
            .addQuery({
              locale,
            })
            .toString()
        : link;
      router.push(url);
    }
  }, [router, shouldDisplaySubscriptionView, locale]);
  const { data, isLoading } = useGQL({
    query: subscribeQuery,
    queryOptions: {
      requestName: NEWSLETTER_SUBSCRIPTION_VIEW_RESOLVER,
    },
    cache: true,
  });
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : new URI(appContext.originalUrl).origin();
  const { trackClickEvent } = useTrackEvent();

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-5 flex h-full w-full flex-col items-center justify-center bg-white">
        <Spinner iconName={ICONS.TRIDENT1} />
      </div>
    );
  }

  addTranslationKeys(data?.config);
  const newsletterInitialValues = {
    ...(data?.newsletters || []).reduce((newslettersCheckbox, newsletter) => {
      newslettersCheckbox[newsletter.id] = isFeatureFlipped(appContext)(
        'newsletter.preCheckedNewsletterPopin',
      );
      return newslettersCheckbox;
    }, {}),
    ...(locale === 'ru-RU'
      ? {
          legalAgreement: false,
        }
      : {}),
  };

  const onSubmit = async (values) => {
    const formattedValues = formatDataForSubscription({
      ...values,
      originCode:
        origin === ORIGIN_PLE
          ? SUBSCRIPTION_ORIGIN.CUSTOMER_ACCOUNT
          : SUBSCRIPTION_ORIGIN.NEWSLETTER,
    });

    if (isEmpty(formattedValues.unicodeFirstName)) {
      formattedValues.unicodeFirstName = formattedValues.firstName;
    }

    if (isEmpty(formattedValues.unicodeLastName)) {
      formattedValues.unicodeLastName = formattedValues.lastName;
    }

    const response = await submitSubscriptionForm({
      appContext,
      data: formattedValues,
      isSubscription: true,
      subscriptionId,
    });
    const error = response?.subscription?.error?.errorDescription || [];

    if (!isEmpty(error)) {
      return setSubmitErrors([error]);
    }

    trackClickEvent(TRACK_EVENT_NAMES.CLICK_NEWSLETTER, {
      detail_click: 'save_infos',
    });
    const res = router.push(t('newsletter.confirmation.href'));
    return res;
  };

  const displayPhoneInput = isFeatureFlipped(appContext)('newsletter.displayPhoneInput');
  const displayEmailInput = isFeatureFlipped(appContext)('newsletter.displayEmailInput');
  const { bookingProcess } = data?.legalInformations || {};
  const cgu2 = bookingProcess?.find(({ id }) => id === 'CGU_2');
  return (
    <Fragment>
      <Head>
        <title key="title">{t('newsletter.seo.title')}</title>
      </Head>
      <TrackPage pageName={TRACK_PAGE_NAMES.CA_NEWSLETTER} />
      <header className="Line Line-Immersive" role="banner">
        <Hero
          size="small"
          title={t('newsletter.immersiveText.0')}
          url={t('newsletter.immersiveBackground')}
        />
      </header>
      <main className="Line" role="main">
        <SiteContainer size="medium">
          <div className="md:flex">
            <Title className="md:w-2/5" iconName={ICONS.MAIL} theme="title-brand-n1">
              {t('newsletter.link.label')}
            </Title>
            <Text className="md:w-3/5" text={t('newsletter.signup.description')} />
          </div>

          <Formik
            initialValues={{ ...newsletterInitialValues, civility: 'M' }}
            onSubmit={onSubmit}
            validationSchema={subscriptionSchema(
              displayEmailInput,
              displayPhoneInput,
              appContext.locale,
            )}
          >
            {({ setFieldValue, isValid, touched, values, errors }) => (
              <Form className="mt-20">
                <div className="md:flex">
                  <legend className="mb-20 text-16 font-bold text-black md:w-2/5">
                    {t('newsletter.signup.personalInformation')}
                  </legend>
                  <div className="md:w-3/5">
                    <NewsletterForm />
                  </div>
                </div>

                <hr className="my-40 border-t-1 border-lightGrey" />

                <div className="md:flex">
                  <legend className="mb-20 text-16 font-bold text-black md:w-2/5">
                    {t('newsletter.signup.subscriptions')}
                  </legend>
                  <div className="flex flex-col gap-15 md:w-3/5">
                    {(data?.newsletters || []).map((checkbox) => (
                      <FormikInput
                        key={checkbox.id}
                        label={checkbox.description}
                        name={checkbox.id}
                        onChange={(e) => onCheckboxChange(e.target, setFieldValue)}
                        type="checkbox"
                        value={checkbox.id}
                      />
                    ))}
                    {['en-CN', 'zh-CN'].includes(appContext.locale) && (
                      <div>
                        <FormikInput
                          label={
                            <Text
                              text={
                                (data?.legalInformations?.newsletter || []).find(
                                  ({ id }) => id === 'PRIVACY_POLICY',
                                )?.label
                              }
                            />
                          }
                          name="privacyPolicyAggreement"
                          type="checkbox"
                        />
                        {touched.privacyPolicyAggreement && !values.privacyPolicyAggreement && (
                          <FormFeedback
                            isError
                            key="d"
                            message={t('newsletter.errorMessages.privacyPolicyNotAccepted')}
                          />
                        )}
                      </div>
                    )}
                    {appContext.locale === 'ru-RU' && cgu2 && (
                      <div>
                        <FormikInput
                          label={
                            <div>
                              <Text text={cgu2.description} />
                              <a
                                className="text-blue ml-5 underline"
                                href={cgu2.url}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                {cgu2.urlLabel}
                              </a>
                            </div>
                          }
                          name="legalAgreement"
                          type="checkbox"
                        />
                        {touched?.legalAgreement && errors?.legalAgreement && (
                          <FormFeedback
                            isError
                            key="legalAgreement-feedback"
                            message={t('booking.participants.errorMessage.legalAgreement')}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="my-40 flex-col items-end md:flex">
                  {!isEmpty(submitErrors) && <FormErrors texts={submitErrors} />}
                  <Button
                    className="w-full md:w-auto"
                    iconRight={{
                      name: ICONS.ARROW_RIGHT,
                    }}
                    isDisabled={
                      !isValid ||
                      !values.CLUBMED ||
                      (['en-CN', 'zh-CN'].includes(appContext.locale) &&
                        !values.privacyPolicyAggreement)
                    }
                    size="large"
                    type="submit"
                  >
                    {t('newsletter.signup.confirm')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          {(data?.legalInformations?.newsletter || []).map(
            (info) =>
              !isEmpty(info.description) && (
                <Text
                  className="text-darkGrey"
                  key={info.id}
                  text={info.description}
                  theme="text-sans-n2"
                />
              ),
          )}
        </SiteContainer>
      </main>
    </Fragment>
  );
};
SubscribeView.pageId = NEWSLETTER_SUBSCRIPTION_PAGE_ID;
SubscribeView.pageType = STATIC_PAGE_TYPES.SHOPPING_NEWSLETTER;
